import DateField from "../../../../common/DateField";
import SelectField from "../../../../common/SelectField";
import TextAreaField from "../../../../common/TextAreaField";
import TextField from "../../../../common/TextField";
import { maskPhoneNumber } from "../../../../../../utils/planManager/planConfigurationUtils.js";

/**
 * Plan Type value that reveals the internal-directory checkbox.
 * @constant {string}
 */
export const OPEN_ACCESS_PLAN_TYPE = "OpenAccess";

/**
 * Plan Type select options.
 * @readonly
 * @type {ReadonlyArray<{ value: string, label: string }>}
 */
export const SETUP_PLAN_TYPE_OPTIONS = Object.freeze([
    { value: "OpenAccess", label: "OpenAccess" },
    { value: "Gated", label: "Gated" },
]);

/**
 * Referral Type select options.
 * @readonly
 * @type {ReadonlyArray<{ value: string, label: string }>}
 */
export const REFERRAL_TYPE_OPTIONS = Object.freeze([
    { value: "Full", label: "Full" },
    { value: "Specialty Only", label: "Specialty Only" },
]);

/**
 * MFA select options.
 * @readonly
 * @type {ReadonlyArray<{ value: string, label: string }>}
 */
export const MFA_OPTIONS = Object.freeze([
    { value: "Forced", label: "Always On" },
    { value: "Default", label: "On - User Can Disable" },
]);

/**
 * PCP designation segmented-control options.
 * @readonly
 * @type {ReadonlyArray<{ value: string, label: string }>}
 */
export const PCP_DESIGNATION_OPTIONS = Object.freeze([
    { value: "required", label: "Required / Optional" },
    { value: "none", label: "None" },
]);

/**
 * Yes/No segmented-control options for boolean radios (Virtual Care).
 * @readonly
 * @type {ReadonlyArray<{ value: boolean, label: string }>}
 */
export const YES_NO_BOOLEAN_OPTIONS = Object.freeze([
    { value: true, label: "Yes" },
    { value: false, label: "No" },
]);

/**
 * Yes/No segmented-control options for the string-valued Referrals radio.
 * @readonly
 * @type {ReadonlyArray<{ value: string, label: string }>}
 */
export const REFERRALS_OPTIONS = Object.freeze([
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
]);

import "./PlanDesign.scss";

/**
 * Stacked native radios matching Plan Setup retrieval (one option per row).
 * @param {object} props
 * @param {string} props.legend - Visible group label.
 * @param {string} props.name - Shared input name for the group.
 * @param {*} props.value - Currently selected option value.
 * @param {(value: *) => void} props.onChange - Emits the selected option value.
 * @param {ReadonlyArray<{ value: *, label: string }>} props.options - Radio options.
 * @param {string} [props.error] - Optional field error.
 * @returns {import('react').JSX.Element}
 */
const StackedRadioGroup = ({
    legend,
    name,
    value,
    onChange,
    options,
    error,
}) => (
    <fieldset
        className={[
            "plan-setup-plan-design__radios",
            error ? "plan-setup-plan-design__radios--error" : null,
        ]
            .filter(Boolean)
            .join(" ")}
    >
        <legend>{legend}</legend>
        {options.map((option) => {
            const optionId = `${name}-${String(option.value)}`;

            return (
                <label
                    key={optionId}
                    className="plan-setup-plan-design__radio"
                    htmlFor={optionId}
                >
                    <input
                        id={optionId}
                        type="radio"
                        name={name}
                        checked={value === option.value}
                        onChange={() => onChange(option.value)}
                    />
                    <span>{option.label}</span>
                </label>
            );
        })}
        {error ? (
            <p className="plan-setup-plan-design__field-error" role="alert">
                {error}
            </p>
        ) : null}
    </fieldset>
);

/**
 * Plan Design step. Presentational — renders labeled form fields for the plan
 * metadata and emits every change upward via `onFieldChange`. Owns no state.
 *
 * @param {object} props
 * @param {Object} props.values - The Plan Design slice of the wizard form state.
 * @param {Record<string, string>} [props.errors] - Field-level validation errors.
 * @param {(field: string, value: *) => void} props.onFieldChange - Emits a single field change.
 * @param {(field: string) => void} [props.onFieldBlur] - Emits field blur event for validation.
 * @returns {import('react').JSX.Element}
 */
const PlanDesign = ({ values, errors = {}, onFieldChange, onFieldBlur }) => {
    const isOpenAccess = values.planType === OPEN_ACCESS_PLAN_TYPE;
    const areReferralsEnabled = values.referrals === "yes";

    return (
        <div className="plan-setup-step plan-setup-plan-design">
            <section className="plan-setup-plan-design__section">
                <TextField
                    id="plan-name"
                    label="Plan Name"
                    value={values.planName}
                    error={errors.planName}
                    onChange={(value) => onFieldChange("planName", value)}
                />

                <div className="plan-setup-plan-design__row">
                    <DateField
                        id="plan-start-date"
                        label="Plan Start Date"
                        value={values.startDate}
                        error={errors.startDate}
                        onChange={(value) => onFieldChange("startDate", value)}
                    />
                    <DateField
                        id="plan-end-date"
                        label="Plan End Date"
                        value={values.endDate}
                        error={errors.endDate}
                        onChange={(value) => onFieldChange("endDate", value)}
                        min={values.startDate || undefined}
                    />
                </div>

                <SelectField
                    id="plan-type"
                    label="Plan Type"
                    value={values.planType}
                    error={errors.planType}
                    onChange={(value) => onFieldChange("planType", value)}
                    placeholderOption="Select a plan type"
                    options={SETUP_PLAN_TYPE_OPTIONS}
                />

                {isOpenAccess && (
                    <label className="plan-setup-plan-design__checkbox">
                        <input
                            type="checkbox"
                            checked={values.fullDirectory}
                            onChange={(event) =>
                                onFieldChange(
                                    "fullDirectory",
                                    event.target.checked,
                                )
                            }
                        />
                        <span>
                            If Open Access, check this box if the plan requires
                            the internal Centivo directory
                        </span>
                    </label>
                )}

                <TextField
                    id="cms-id"
                    label="CMS ID"
                    value={values.cmsId}
                    error={errors.cmsId}
                    onChange={(value) => onFieldChange("cmsId", value)}
                />

                <TextField
                    id="cms-name"
                    label="CMS Name"
                    value={values.cmsName}
                    error={errors.cmsName}
                    onChange={(value) => onFieldChange("cmsName", value)}
                />
            </section>

            <section className="plan-setup-plan-design__section">
                <TextField
                    id="contact-number"
                    label="Centivo contact us phone number"
                    placeholder="XXX-XXX-XXXX"
                    maxLength={12}
                    value={values.contactNumber}
                    error={errors.contactNumber}
                    onChange={(value) =>
                        onFieldChange("contactNumber", maskPhoneNumber(value))
                    }
                    onBlur={() => onFieldBlur?.("contactNumber")}
                />

                <TextField
                    id="support-hours"
                    label="Member Care contact us hours"
                    value={values.supportHours}
                    error={errors.supportHours}
                    onChange={(value) => onFieldChange("supportHours", value)}
                />

                <TextField
                    id="sender-email"
                    label="Sender Email"
                    value={values.senderEmail}
                    error={errors.senderEmail}
                    onChange={(value) => onFieldChange("senderEmail", value)}
                />

                <TextField
                    id="portal-url"
                    label="Portal URL"
                    value={values.portalUrl}
                    error={errors.portalUrl}
                    onChange={(value) => onFieldChange("portalUrl", value)}
                />
            </section>

            <section className="plan-setup-plan-design__section">
                <StackedRadioGroup
                    legend="PCP designation"
                    name="pcp-designation"
                    value={values.pcpDesignation}
                    error={errors.pcpDesignation}
                    onChange={(value) => onFieldChange("pcpDesignation", value)}
                    options={PCP_DESIGNATION_OPTIONS}
                />

                <StackedRadioGroup
                    legend="Virtual Care"
                    name="virtual-care"
                    value={values.vpcEnabled}
                    error={errors.vpcEnabled}
                    onChange={(value) => onFieldChange("vpcEnabled", value)}
                    options={YES_NO_BOOLEAN_OPTIONS}
                />

                <StackedRadioGroup
                    legend="Referrals"
                    name="referrals"
                    value={values.referrals}
                    error={errors.referrals}
                    onChange={(value) => onFieldChange("referrals", value)}
                    options={REFERRALS_OPTIONS}
                />

                <SelectField
                    id="referral-type"
                    label="Referral Type (if applicable)"
                    value={values.referralType}
                    error={errors.referralType}
                    onChange={(value) => onFieldChange("referralType", value)}
                    placeholderOption="Select a referral type"
                    options={REFERRAL_TYPE_OPTIONS}
                    disabled={!areReferralsEnabled}
                />

                <SelectField
                    id="mfa"
                    label="MFA"
                    value={values.mfa}
                    error={errors.mfa}
                    onChange={(value) => onFieldChange("mfa", value)}
                    placeholderOption="Select a MFA option"
                    options={MFA_OPTIONS}
                />
            </section>

            <section className="plan-setup-plan-design__section">
                <p className="plan-setup-plan-design__section-title">
                    Password Requirements
                </p>

                <TextAreaField
                    id="password-helper"
                    label="Password Helper Text"
                    value={values.passwordHelperText}
                    error={errors.passwordHelperText}
                    onChange={(value) =>
                        onFieldChange("passwordHelperText", value)
                    }
                    onBlur={() => onFieldBlur?.("passwordHelperText")}
                />

                <div className="form-input">
                    <label htmlFor="min-characters">
                        Minimum Number of Characters
                    </label>
                    <input
                        id="min-characters"
                        type="number"
                        min="8"
                        value={values.minCharactersNo}
                        onChange={(event) =>
                            onFieldChange("minCharactersNo", event.target.value)
                        }
                    />
                    {errors.minCharactersNo ? (
                        <p
                            className="plan-setup-plan-design__field-error"
                            role="alert"
                        >
                            {errors.minCharactersNo}
                        </p>
                    ) : null}
                </div>

                <div className="form-input">
                    <label htmlFor="special-characters">
                        Number of Special Characters
                    </label>
                    <input
                        id="special-characters"
                        type="number"
                        min="1"
                        value={values.specialCharactersNo}
                        onChange={(event) =>
                            onFieldChange(
                                "specialCharactersNo",
                                event.target.value,
                            )
                        }
                    />
                    {errors.specialCharactersNo ? (
                        <p
                            className="plan-setup-plan-design__field-error"
                            role="alert"
                        >
                            {errors.specialCharactersNo}
                        </p>
                    ) : null}
                </div>

                <div className="form-input">
                    <label htmlFor="letters-count">Number of Letters</label>
                    <input
                        id="letters-count"
                        type="number"
                        min="1"
                        value={values.lettersNo}
                        onChange={(event) =>
                            onFieldChange("lettersNo", event.target.value)
                        }
                    />
                    {errors.lettersNo ? (
                        <p
                            className="plan-setup-plan-design__field-error"
                            role="alert"
                        >
                            {errors.lettersNo}
                        </p>
                    ) : null}
                </div>

                <div className="form-input">
                    <label htmlFor="numbers-count">Number of Numbers</label>
                    <input
                        id="numbers-count"
                        type="number"
                        min="1"
                        value={values.numbersNo}
                        onChange={(event) =>
                            onFieldChange("numbersNo", event.target.value)
                        }
                    />
                    {errors.numbersNo ? (
                        <p
                            className="plan-setup-plan-design__field-error"
                            role="alert"
                        >
                            {errors.numbersNo}
                        </p>
                    ) : null}
                </div>
            </section>
        </div>
    );
};

export default PlanDesign;
